import React from 'react';
import { useNode } from '@craftjs/core';
import { isBlankHtml } from '@/utils/functions';
import { htmlSeguro } from '../utils/htmlSeguro';

export type ViñetaLista = 'check' | 'dot' | 'arrow' | 'star' | 'none';

interface ItemLista {
  text?: string;
}

interface ListBlockProps {
  items?: ItemLista[];
  bullet?: ViñetaLista;
  bulletColor?: string;
  textColor?: string;
  fontSize?: number;
  columns?: number;
  gap?: number;
  padding?: number;
}

/**
 * Las viñetas se dibujan a mano y no con una librería de íconos: así el markup
 * es idéntico en los dos repos y no hay que mantener otro espejo más.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
export const Vineta = ({ tipo, color, tamano }: { tipo: ViñetaLista; color: string; tamano: number }) => {
  if (tipo === 'none') return null;
  const lado = Math.round(tamano * 1.1);
  const comun = {
    width: lado,
    height: lado,
    viewBox: '0 0 20 20',
    fill: 'none',
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    style: { flexShrink: 0, marginTop: Math.round(tamano * 0.15) },
    'aria-hidden': true,
  };
  if (tipo === 'dot') {
    return <svg {...comun} fill={color} stroke="none"><circle cx="10" cy="10" r="4" /></svg>;
  }
  if (tipo === 'arrow') {
    return <svg {...comun}><path d="M5 10h10M11 6l4 4-4 4" /></svg>;
  }
  if (tipo === 'star') {
    return <svg {...comun} fill={color} stroke="none"><path d="M10 2l2.4 5 5.6.8-4 3.9 1 5.5-5-2.6-5 2.6 1-5.5-4-3.9 5.6-.8z" /></svg>;
  }
  return <svg {...comun}><path d="M4 10.5l4 4 8-8.5" /></svg>;
};

/**
 * Lista con viñetas — render del sitio público.
 *
 * Beneficios, características, requisitos: lo que en cualquier builder se
 * resuelve con una lista. Se puede mostrar en 1, 2 o 3 columnas; en móvil
 * siempre queda en una.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/ListBlock.tsx.
 */
export const ListBlock = ({
  items = [],
  bullet = 'check',
  bulletColor = '#1e88e5',
  textColor = '#374151',
  fontSize = 16,
  columns = 1,
  gap = 12,
  padding = 0,
}: ListBlockProps) => {
  const { connectors } = useNode();

  const cols = Math.max(1, Math.min(3, Number(columns) || 1));
  const tamano = Math.max(10, Number(fontSize) || 16);
  const visibles = (items ?? []).filter((item) => !isBlankHtml(item?.text));

  return (
    <ul
      ref={(el: HTMLUListElement | null) => { if (el) connectors.connect(el); }}
      className="grid w-full list-none grid-cols-1 sm:[grid-template-columns:repeat(var(--cols-lista),minmax(0,1fr))]"
      style={{
        ['--cols-lista' as string]: String(cols),
        gap: `${gap}px`,
        padding: `${padding}px`,
        margin: 0,
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
      }}
    >
      {visibles.map((item, i) => (
        <li key={i} className="flex items-start" style={{ gap: `${Math.round(tamano * 0.5)}px` }}>
          <Vineta tipo={bullet} color={bulletColor} tamano={tamano} />
          <span
            className="leading-relaxed"
            style={{ color: textColor, fontSize: `${tamano}px` }}
            dangerouslySetInnerHTML={htmlSeguro(item.text || '')}
          />
        </li>
      ))}
    </ul>
  );
};

ListBlock.craft = {
  displayName: 'ListBlock',
  props: {
    items: [
      { text: 'Primer punto de la lista' },
      { text: 'Segundo punto de la lista' },
      { text: 'Tercer punto de la lista' },
    ],
    bullet: 'check',
    bulletColor: '#1e88e5',
    textColor: '#374151',
    fontSize: 16,
    columns: 1,
    gap: 12,
    padding: 0,
  },
};
